/**
 * Canonical asset-import CSV template (SIL-15).
 *
 * Single source of truth shared by three consumers so they cannot drift:
 *   - the API upload validator      (apps/api/src/routes/client/import.ts)
 *   - the customer-portal template download + on-screen column reference
 *                                    (apps/customer/src/routes/onboarding.tsx)
 *   - the drift/consistency test     (apps/api ... import.unit.test.ts)
 *
 * Editing a column, enum, or the example row here updates all three at once.
 * Pure TS (no DB import) so it is safe in both the Lambda and the browser.
 */

/** Allowed `condition_band` values (raw staging text; enforced on import). */
export const ASSET_IMPORT_CONDITION_BANDS = [
  'like_new',
  'normal_enterprise_wear',
  'high_wear',
  'unknown',
] as const;
export type AssetImportConditionBand = (typeof ASSET_IMPORT_CONDITION_BANDS)[number];

/**
 * Words a seller naturally writes that map onto a canonical band (SIL-1589).
 * `new` is an ordinary word for a new-equipment seller. Matched
 * case-insensitively; the canonical value is what gets staged.
 */
export const ASSET_IMPORT_CONDITION_BAND_ALIASES: Readonly<Record<string, AssetImportConditionBand>> = {
  new: 'like_new',
};

/**
 * Allowed `lifecycle_state` values (raw staging text; enforced on import).
 * Single-sourced from the `asset_lifecycle_state` table MINUS `sold` (set only
 * by the SX exchange, never importable). A drift test
 * (packages/shared/src/__tests__/import-template-schema-drift.test.ts) parses
 * db/schema/*.sql and fails when the table and this list diverge (SIL-1589).
 */
export const ASSET_IMPORT_LIFECYCLE_STATES = [
  'active',
  'idle',
  'in_transit',
  'decommissioned',
  'disposed',
  'quarantined',
] as const;
export type AssetImportLifecycleState = (typeof ASSET_IMPORT_LIFECYCLE_STATES)[number];

/** `asset_lifecycle_state` ids a seller may NOT import (drift test input). */
export const ASSET_IMPORT_EXCLUDED_LIFECYCLE_STATES = ['sold'] as const;

/**
 * Importable asset classes: exactly the `asset_class` table ids (promote does
 * `lower(asset_class_raw)` into an FK, so anything else fails the whole
 * batch). Drift-tested against db/schema/*.sql like the lifecycle list.
 */
export const ASSET_IMPORT_ASSET_CLASSES = [
  { id: 'gpu', label: 'GPU' },
  { id: 'server', label: 'Server' },
  { id: 'storage', label: 'Storage' },
  { id: 'ethernet_switch', label: 'Ethernet switch' },
  { id: 'ib_switch', label: 'InfiniBand switch' },
  { id: 'nvlink_switch', label: 'NVLink switch' },
  { id: 'router', label: 'Router' },
  { id: 'cpu', label: 'CPU' },
  { id: 'memory', label: 'Memory' },
  { id: 'nic', label: 'NIC' },
  { id: 'dpu', label: 'DPU' },
  { id: 'rack_system', label: 'Rack system' },
  { id: 'gaming_pc', label: 'Gaming PC' },
  { id: 'cloud_vm', label: 'Cloud VM' },
] as const;
export type AssetImportAssetClassId = (typeof ASSET_IMPORT_ASSET_CLASSES)[number]['id'];

/**
 * Placeholder serial shipped in the template's example row. The download tells
 * users to replace it; any imported row still carrying it is the untouched
 * example and is skipped (content-based, not positional — a user who typed a
 * real serial in that row is validated normally).
 */
export const ASSET_IMPORT_SENTINEL_SERIAL = 'REPLACE-WITH-YOUR-SERIAL';

export interface AssetImportColumn {
  key: string;
  requirement: 'required' | 'recommended' | 'optional';
  /** Allowed values for enum columns; undefined = free text. */
  allowed?: readonly string[];
  /** Value used in the downloadable example row. */
  example: string;
  /** One-line hint for the on-screen column reference. */
  hint: string;
}

/** Ordered template columns. Header order == this order. */
export const ASSET_IMPORT_COLUMNS: readonly AssetImportColumn[] = [
  {
    key: 'serial_number',
    requirement: 'required',
    example: ASSET_IMPORT_SENTINEL_SERIAL,
    hint: 'Unique hardware serial or service tag (not the part number). The only required field.',
  },
  {
    key: 'manufacturer',
    requirement: 'recommended',
    example: 'NVIDIA',
    hint: 'Make, e.g. Cisco. Recommended — lets us match the asset to a catalog SKU.',
  },
  {
    key: 'model',
    requirement: 'recommended',
    example: 'H100 SXM',
    hint: 'Model, e.g. Catalyst 9500. Recommended — improves SKU matching and pricing.',
  },
  {
    key: 'condition_band',
    requirement: 'optional',
    allowed: ASSET_IMPORT_CONDITION_BANDS,
    example: 'like_new',
    hint: 'Optional condition grade. "new" is accepted and recorded as like_new.',
  },
  {
    key: 'lifecycle_state',
    requirement: 'optional',
    allowed: ASSET_IMPORT_LIFECYCLE_STATES,
    example: 'active',
    hint: 'Optional current lifecycle state. Unsold new stock: use idle.',
  },
  {
    key: 'data_center_code',
    requirement: 'optional',
    example: 'DC-1',
    hint: 'Optional site/location code. Any extra columns you add are kept as attributes.',
  },
  {
    key: 'asset_tag',
    requirement: 'optional',
    example: 'IT-00042',
    hint: 'Optional inventory/asset tag, unique within your org and within the file (not a site code shared by every row). Lets a later ERP financial import match this asset by tag.',
  },
] as const;

/** Header row column keys, in order. */
export const ASSET_IMPORT_HEADER: readonly string[] = ASSET_IMPORT_COLUMNS.map((c) => c.key);

/** RFC-4180 escape: quote a field only if it contains comma, quote, or newline. */
function csvField(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** The full downloadable template: header + one example row, `\n`-terminated. */
export function assetImportTemplateCsv(): string {
  const header = ASSET_IMPORT_HEADER.map(csvField).join(',');
  const example = ASSET_IMPORT_COLUMNS.map((c) => csvField(c.example)).join(',');
  return `${header}\n${example}\n`;
}
